import React, { useState } from 'react';
import PaintingHeader from './PaintingHeader';
import PaintingFooter from './PaintingFooter';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    serviceType: 'residential-interior',
    projectDetails: '',
    preferredContact: 'phone'
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${apiUrl}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Quote request received! We\'ll contact you within 24 hours.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          serviceType: 'residential-interior',
          projectDetails: '',
          preferredContact: 'phone'
        });
      } else {
        throw new Error(data.message || 'Failed to submit quote request');
      }
    } catch (error) {
      toast.error('Something went wrong. Please call us at (954) 817-2046 instead.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      <PaintingHeader />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4">
            Get Your Free Quote
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Fill out the form below or call us directly. We respond to all inquiries within 24 hours!
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-2 hover:border-[#1E3A8A] transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#1E3A8A] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-2">Call Us</h3>
                      <a href="tel:+19548172046" className="text-[#1E3A8A] hover:text-[#EA580C] font-semibold text-lg">
                        (954) 817-2046
                      </a>
                      <p className="text-sm text-[#64748B] mt-1">
                        Mon-Fri: 7AM - 6PM<br/>
                        Sat: 8AM - 4PM
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-[#1E3A8A] transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#1E3A8A] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-2">Email Us</h3>
                      <a href="mailto:info@destepaintingservice.com" className="text-[#1E3A8A] hover:text-[#EA580C] font-semibold break-all">
                        info@destepaintingservice.com
                      </a>
                      <p className="text-sm text-[#64748B] mt-1">
                        We'll respond within 24 hours
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-[#1E3A8A] transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-[#1E3A8A] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0F172A] mb-2">Visit Us</h3>
                      <p className="text-[#64748B]">
                        1841 Rodman St<br/>
                        Hollywood, FL 33020
                      </p>
                      <a 
                        href="https://maps.google.com/?q=1841+Rodman+St,Hollywood,FL+33020" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#1E3A8A] hover:text-[#EA580C] text-sm font-semibold mt-2 inline-block"
                      >
                        Get Directions →
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#1E3A8A] text-white border-0">
                <CardContent className="p-6">
                  <Clock className="w-8 h-8 mb-4" />
                  <h3 className="font-bold mb-3">Business Hours</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Monday - Friday</span>
                      <span className="font-semibold">7AM - 6PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday</span>
                      <span className="font-semibold">8AM - 4PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday</span>
                      <span className="font-semibold">Closed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quote Request Form */}
            <div className="lg:col-span-2">
              <Card className="border-2">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#0F172A] mb-6">
                    Request a Free Quote
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Doe"
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="(954) 123-4567"
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="address">Project Address</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="123 Main St, Hollywood, FL"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="serviceType">Service Type *</Label>
                      <select
                        id="serviceType"
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={handleChange}
                        required
                        className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                      >
                        <option value="residential-interior">Residential Interior Painting</option>
                        <option value="residential-exterior">Residential Exterior Painting</option>
                        <option value="commercial">Commercial Painting</option>
                        <option value="cabinet-refinishing">Cabinet Refinishing</option>
                        <option value="deck-staining">Deck Staining</option>
                        <option value="pressure-washing">Pressure Washing</option>
                        <option value="drywall-repair">Drywall Repair</option>
                        <option value="other">Other Service</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="projectDetails">Project Details</Label>
                      <Textarea
                        id="projectDetails"
                        name="projectDetails"
                        value={formData.projectDetails}
                        onChange={handleChange}
                        placeholder="Tell us about your project... (number of rooms, square footage, timeline, etc.)"
                        rows={4}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label>Preferred Contact Method *</Label>
                      <div className="mt-2 space-x-6">
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            name="preferredContact"
                            value="phone"
                            checked={formData.preferredContact === 'phone'}
                            onChange={handleChange}
                            className="w-4 h-4 text-[#1E3A8A]"
                          />
                          <span className="ml-2">Phone</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            name="preferredContact"
                            value="email"
                            checked={formData.preferredContact === 'email'}
                            onChange={handleChange}
                            className="w-4 h-4 text-[#1E3A8A]"
                          />
                          <span className="ml-2">Email</span>
                        </label>
                      </div>
                    </div>

                    <div className="pt-4">
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white text-lg py-6"
                      >
                        {loading ? (
                          'Sending...'
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Request Free Quote
                          </>
                        )}
                      </Button>
                      <p className="text-sm text-[#64748B] text-center mt-4">
                        * We'll respond within 24 hours with your free estimate
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (placeholder) */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-4">
              Serving Hollywood, FL & Surrounding Areas
            </h2>
            <p className="text-[#64748B]">
              Broward County | Fort Lauderdale | Pembroke Pines | Miramar | Hallandale Beach
            </p>
          </div>
          <div className="aspect-video bg-[#F1F5F9] rounded-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3585.4537!2d-80.149!3d26.011!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDAwJzQwLjAiTiA4MMKwMDgnNTYuNCJX!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="D'este Painting Location"
            ></iframe>
          </div>
        </div>
      </section>

      <PaintingFooter />
    </div>
  );
};

export default Contact;
